import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, User, Wallet, ShoppingCart, ExternalLink, Pencil } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Field, Input, Select } from '@/components/ui/Field';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { adminCustomerApi } from '@/lib/adminCustomerApi';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../../nav';

const GRADES = ['Bronze', 'Silver', 'Gold', 'Platinum'] as const;

const GRADE_TONE: Record<string, 'neutral' | 'info' | 'warning' | 'primary'> = {
  Bronze: 'neutral',
  Silver: 'info',
  Gold: 'warning',
  Platinum: 'primary',
};
const PROVIDER_LABEL: Record<string, string> = {
  email: 'Email',
  google: 'Google',
  facebook: 'Facebook',
  apple: 'Apple',
};
const PROVIDER_ICON: Record<string, string> = {
  google: 'ri-google-fill',
  facebook: 'ri-facebook-fill',
  apple: 'ri-apple-fill',
};
const STATUS_TONE: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'neutral'> = {
  placed: 'primary',
  confirmed: 'primary',
  shipped: 'warning',
  dispatched: 'warning',
  delivered: 'success',
  cod_collected: 'success',
  remitted: 'success',
  settled: 'success',
  cancelled: 'error',
  returned: 'error',
};

interface CustomerProfile {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  member_grade: string;
  marketing_consent: boolean;
  created_at: string;
  signup_provider: string;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  area: string | null;
  postal_code: string | null;
}

interface MileageRow {
  id: string;
  type: string;
  amount: number;
  note: string | null;
  created_at: string;
}

interface OrderRow {
  id: string;
  order_no: string;
  status: string;
  total_bdt: number;
  placed_at: string;
}

export default function RxkCustomerDetail() {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const { push } = useToast();
  const [customer, setCustomer] = useState<CustomerProfile | null>(null);
  const [mileage, setMileage] = useState<MileageRow[]>([]);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [editForm, setEditForm] = useState({ fullName: '', phone: '', email: '', memberGrade: 'Bronze', newPassword: '' });

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    const [custRes, mileageRes, orderRes] = await Promise.all([
      supabase
        .from('rxk_customers')
        .select('id, full_name, email, phone, member_grade, marketing_consent, created_at, address_line1, address_line2, city, area, postal_code, signup_provider')
        .eq('id', customerId)
        .maybeSingle(),
      supabase.from('rxk_mileage_ledger').select('id, type, amount, note, created_at').eq('customer_id', customerId).order('created_at', { ascending: false }),
      supabase.from('core_orders').select('id, order_no, status, total_bdt, placed_at').eq('customer_id', customerId).order('placed_at', { ascending: false }),
    ]);
    setCustomer((custRes.data as CustomerProfile) ?? null);
    setMileage((mileageRes.data as MileageRow[]) ?? []);
    setOrders((orderRes.data as OrderRow[]) ?? []);
    setLoading(false);
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = () => {
    if (!customer) return;
    setEditForm({
      fullName: customer.full_name,
      phone: customer.phone ?? '',
      email: customer.email ?? '',
      memberGrade: customer.member_grade,
      newPassword: '',
    });
    setEditOpen(true);
  };

  const handleEditSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!customer || !customerId) return;
    setEditBusy(true);
    try {
      const patch: Record<string, unknown> = {};
      if (editForm.fullName !== customer.full_name) patch.full_name = editForm.fullName;
      if (editForm.phone !== (customer.phone ?? '')) patch.phone = editForm.phone || null;
      if (editForm.memberGrade !== customer.member_grade) patch.member_grade = editForm.memberGrade;

      if (Object.keys(patch).length > 0) {
        const { error } = await supabase.from('rxk_customers').update(patch).eq('id', customerId);
        if (error) throw error;
      }

      if (editForm.email !== (customer.email ?? '')) {
        await adminCustomerApi.updateEmail(customerId, editForm.email);
      }

      if (editForm.newPassword.trim()) {
        await adminCustomerApi.setPassword(customerId, editForm.newPassword.trim());
      }

      push('success', 'Customer updated');
      setEditOpen(false);
      await load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to update customer');
    } finally {
      setEditBusy(false);
    }
  };

  if (loading) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <Spinner />
      </AppShell>
    );
  }

  if (!customer) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <EmptyState title="Customer not found" />
      </AppShell>
    );
  }

  const mileageBalance = mileage.reduce((sum, m) => sum + Number(m.amount), 0);

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/customers')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-faint cursor-pointer" aria-label="Back to customers">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1">
            <h1 className="text-[20px] font-bold text-ink">{customer.full_name}</h1>
            <p className="text-[12px] text-ink-faint">{customer.email} · Joined {customer.created_at.slice(0, 10)}</p>
          </div>
          {customer.signup_provider && customer.signup_provider !== 'email' && (
            <Badge tone="neutral">
              <i className={`${PROVIDER_ICON[customer.signup_provider] ?? ''} mr-1`}></i>
              {PROVIDER_LABEL[customer.signup_provider] ?? customer.signup_provider}
            </Badge>
          )}
          <Badge tone={GRADE_TONE[customer.member_grade] ?? 'neutral'}>{customer.member_grade}</Badge>
          <Button size="sm" variant="secondary" onClick={openEdit}>
            <Pencil className="w-3.5 h-3.5" /> Edit
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">
            <Card>
              <CardHeader title="Orders (주문)" icon={<ShoppingCart className="w-4 h-4" />} subtitle={`${orders.length} order(s)`} />
              <CardBody className="space-y-1">
                {orders.length === 0 ? (
                  <EmptyState title="No orders yet" />
                ) : (
                  orders.map((o) => (
                    <Link
                      key={o.id}
                      to={`/admin/orders/${o.id}`}
                      className="flex items-center justify-between px-2 py-2 rounded-lg hover:bg-surface-muted transition-colors duration-150"
                    >
                      <div>
                        <p className="text-[12.5px] font-semibold text-ink flex items-center gap-1.5">
                          {o.order_no} <ExternalLink className="w-3 h-3 text-ink-faint" />
                        </p>
                        <p className="text-[11px] text-ink-faint">{o.placed_at.slice(0, 10)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge tone={STATUS_TONE[o.status] ?? 'neutral'}>{o.status}</Badge>
                        <p className="text-[12.5px] font-bold text-ink tabular-nums">৳{o.total_bdt.toLocaleString()}</p>
                      </div>
                    </Link>
                  ))
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Mileage Ledger (마일리지 내역)" icon={<Wallet className="w-4 h-4" />} subtitle={`${mileage.length} entries`} />
              <CardBody className="space-y-1">
                {mileage.length === 0 ? (
                  <EmptyState title="No mileage activity yet" />
                ) : (
                  mileage.map((m) => (
                    <div key={m.id} className="flex items-center justify-between px-2 py-2 rounded-lg hover:bg-surface-muted">
                      <div>
                        <p className="text-[12.5px] font-semibold text-ink">{m.note ?? m.type}</p>
                        <p className="text-[11px] text-ink-faint">{m.created_at.slice(0, 10)} · {m.type}</p>
                      </div>
                      <p className={`text-[12.5px] font-bold tabular-nums ${m.amount >= 0 ? 'text-success' : 'text-error'}`}>
                        {m.amount >= 0 ? '+' : ''}{m.amount} BDT
                      </p>
                    </div>
                  ))
                )}
              </CardBody>
            </Card>
          </div>

          <div className="space-y-5">
            <Card>
              <CardHeader title="Profile (프로필)" icon={<User className="w-4 h-4" />} />
              <CardBody className="space-y-1.5 text-[12.5px]">
                <div className="flex items-center justify-between">
                  <span className="text-ink-faint">Phone</span>
                  <span className="text-ink font-semibold">{customer.phone ?? '—'}</span>
                </div>
                {customer.address_line1 && (
                  <div className="pt-1">
                    <span className="text-ink-faint block mb-0.5">Address</span>
                    <span className="text-ink font-semibold">
                      {[customer.address_line1, customer.address_line2, customer.area, customer.city, customer.postal_code]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-ink-faint">Marketing</span>
                  <Badge tone={customer.marketing_consent ? 'success' : 'neutral'}>{customer.marketing_consent ? 'opted in' : 'opted out'}</Badge>
                </div>
                <div className="flex items-center justify-between pt-1.5 mt-1.5 border-t border-border-subtle">
                  <span className="text-ink-faint">Mileage Balance</span>
                  <span className="text-ink font-bold tabular-nums">{mileageBalance} BDT</span>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit Customer"
        subtitle={customer.full_name}
        icon={<Pencil className="w-3.5 h-3.5" />}
        busy={editBusy}
        footer={
          <>
            <Button type="button" variant="secondary" size="md" className="flex-1" onClick={() => setEditOpen(false)} disabled={editBusy}>
              Cancel
            </Button>
            <Button type="submit" form="edit-customer-form" size="md" className="flex-1" loading={editBusy}>
              Save
            </Button>
          </>
        }
      >
        <form id="edit-customer-form" onSubmit={handleEditSubmit} className="space-y-3.5">
          <Field label="Full name">
            <Input value={editForm.fullName} onChange={(e) => setEditForm((f) => ({ ...f, fullName: e.target.value }))} required />
          </Field>
          <Field label="Phone">
            <Input value={editForm.phone} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} />
          </Field>
          <Field label="Email">
            <Input type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} required />
          </Field>
          <Field label="Member grade">
            <Select value={editForm.memberGrade} onChange={(e) => setEditForm((f) => ({ ...f, memberGrade: e.target.value }))}>
              {GRADES.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="New password (leave blank to keep current)">
            <Input
              type="password"
              value={editForm.newPassword}
              onChange={(e) => setEditForm((f) => ({ ...f, newPassword: e.target.value }))}
              placeholder="••••••••"
              minLength={6}
            />
          </Field>
        </form>
      </Modal>
    </AppShell>
  );
}
